import {
  ADD_DELIVERABLE_TYPE,
  AddDeliverableType,
  EXTENDED_SPECIAL_CHARS,
  INPUT_LIMIT,
} from "../../../../../helpers/constants";
import { specialCharactersNotAllowedValidation } from "../../../../../helpers/constants/commonValidationSchemaUtils";
import { UnitTypesKey } from "../../../../../helpers/constants/siteCoreKeys";
import { KeyValueMap } from "../../../../../redux/slices/appState/models";
import {
  CreateActivityDefaultFormKeys,
  CreateActivityGeneralFormKeys,
  CreateDeliverablesFormKeys,
  UpdateDeliverablesFormKeys,
} from "./models";

import * as yup from "yup";

export const deliverableNameValidation = (deliverablesValue: string) =>
  !deliverablesValue ||
  INPUT_LIMIT.ALPHA_NUMERIC_WITH_EXTENDED_SPECIAL_CHARS.test(deliverablesValue);

export const UpdateDeliverableValidationSchema = (
  commonValidationMessages: KeyValueMap,
) =>
  yup.object().shape({
    [UpdateDeliverablesFormKeys.measurementMinute]: yup
      .string()
      .when([UpdateDeliverablesFormKeys.units], {
        is: (unitValue: string) => {
          return unitValue === UnitTypesKey.Minutes;
        },
        then: (schema) =>
          schema.required(commonValidationMessages.requiredValidation),
      })
      .default(""),
    [UpdateDeliverablesFormKeys.measurementOther]: yup
      .number()
      .nullable()
      .when([UpdateDeliverablesFormKeys.units], {
        is: (unitValue: string) => {
          return (
            unitValue === UnitTypesKey.Count || unitValue === UnitTypesKey.Days
          );
        },
        then: (schema) =>
          schema
            .required(commonValidationMessages.requiredValidation)
            .typeError(commonValidationMessages.requiredValidation)
            .min(1, commonValidationMessages.valueRangeFieldValidator1To99)
            .max(99, commonValidationMessages.valueRangeFieldValidator1To99),
      })
      .default(0),
  });

export const AddDeliverableValidationSchema = (
  commonValidationMessages: KeyValueMap,
  userLang: string,
) =>
  yup.object().shape({
    [CreateDeliverablesFormKeys.addDeliverableType]: yup
      .mixed<AddDeliverableType>()
      .oneOf([
        ADD_DELIVERABLE_TYPE.newKey as AddDeliverableType,
        ADD_DELIVERABLE_TYPE.existingKey as AddDeliverableType,
      ])
      .required()
      .default(ADD_DELIVERABLE_TYPE.newKey as AddDeliverableType),

    [CreateDeliverablesFormKeys.deliverableType]: yup
      .string()
      .when([CreateDeliverablesFormKeys.addDeliverableType], {
        is: (addDeliverableType: AddDeliverableType) =>
          addDeliverableType === ADD_DELIVERABLE_TYPE.newKey,
        then: (schema) =>
          schema.required(commonValidationMessages.requiredValidation),
        otherwise: (schema) => schema.notRequired(),
      }),

    [`${CreateDeliverablesFormKeys.name}.${userLang}`]: yup
      .string()
      .test(
        "required-when-new",
        commonValidationMessages.requiredValidation,
        function () {
          const { parent } = this;
          const deliverablesValue = parent[CreateDeliverablesFormKeys.name][
            userLang
          ] as string;
          const addDeliverableType =
            parent[CreateDeliverablesFormKeys.addDeliverableType];
          if (addDeliverableType === ADD_DELIVERABLE_TYPE.newKey) {
            return !!deliverablesValue?.trim();
          }
          return true;
        },
      )
      .test(
        EXTENDED_SPECIAL_CHARS,
        commonValidationMessages.fieldContainsInvalidCharactersErrorMessage,
        function () {
          const { parent } = this;
          const deliverablesValue = parent[CreateDeliverablesFormKeys.name][
            userLang
          ] as string;

          return deliverableNameValidation(deliverablesValue);
        },
      ),
    [CreateDeliverablesFormKeys.units]: yup
      .string()
      .required(commonValidationMessages.requiredValidation)
      .when([CreateDeliverablesFormKeys.addDeliverableType], {
        is: (addDeliverableType: AddDeliverableType) =>
          addDeliverableType === ADD_DELIVERABLE_TYPE.newKey,
        then: (schema) =>
          schema.required(commonValidationMessages.requiredValidation),
        otherwise: (schema) => schema.notRequired(),
      }),

    [CreateDeliverablesFormKeys.measurementMinute]: yup
      .string()
      .nullable()
      .when(
        [
          CreateDeliverablesFormKeys.units,
          CreateDeliverablesFormKeys.addDeliverableType,
        ],
        {
          is: (unitValue: string, addDeliverableType: AddDeliverableType) =>
            addDeliverableType === ADD_DELIVERABLE_TYPE.newKey &&
            unitValue === UnitTypesKey.Minutes,
          then: (schema) =>
            schema
              .required(commonValidationMessages.requiredValidation)
              .default(""),
          otherwise: (schema) => schema.notRequired(),
        },
      ),

    [CreateDeliverablesFormKeys.measurementOther]: yup
      .number()
      .nullable()
      .when(
        [
          CreateDeliverablesFormKeys.units,
          CreateDeliverablesFormKeys.addDeliverableType,
        ],
        {
          is: (unitValue: string, addDeliverableType: AddDeliverableType) =>
            addDeliverableType === ADD_DELIVERABLE_TYPE.newKey &&
            (unitValue === UnitTypesKey.Count ||
              unitValue === UnitTypesKey.Days),
          then: (schema) =>
            schema
              .required(commonValidationMessages.requiredValidation)
              .typeError(commonValidationMessages.requiredValidation)
              .min(1, commonValidationMessages.valueRangeFieldValidator1To99)
              .max(99, commonValidationMessages.valueRangeFieldValidator1To99)
              .default(0),
          otherwise: (schema) => schema.notRequired(),
        },
      ),
  });

export const CreateActivityGeneralValidationSchema = (
  commonValidationMessages: KeyValueMap,
  userLang: string,
) =>
  yup.object().shape({
    [CreateActivityGeneralFormKeys.candidateStatuses]: yup
      .array()
      .min(1, commonValidationMessages.requiredValidation)
      .default([]),
    [CreateActivityGeneralFormKeys.activityType]: yup
      .string()
      .required(commonValidationMessages.requiredValidation),
    [CreateActivityGeneralFormKeys.modality]: yup.array().default([]),
    [CreateActivityGeneralFormKeys.topic]: yup.object().shape({
      [userLang]: specialCharactersNotAllowedValidation(
        commonValidationMessages.requiredValidation,
        commonValidationMessages.fieldContainsInvalidCharactersErrorMessage,
      ),
    }),
    [CreateActivityGeneralFormKeys.deliverables]: yup.array().default([]),
    [CreateActivityGeneralFormKeys.activityStatus]: yup
      .string()
      .required(commonValidationMessages.requiredValidation),
    [CreateActivityGeneralFormKeys.triggerBilling]: yup
      .boolean()
      .default(false),
    [CreateActivityGeneralFormKeys.lastContactDate]: yup
      .boolean()
      .default(false),
    [CreateActivityGeneralFormKeys.triggerTouch]: yup.boolean().default(false),
  });

export const CreateActivityDefaultValidationSchema = (
  commonValidationMessages: KeyValueMap,
) =>
  yup.object().shape({
    [CreateActivityDefaultFormKeys.units]: yup
      .string()
      .required(commonValidationMessages.requiredValidation),
    [CreateActivityDefaultFormKeys.measurementMinute]: yup
      .string()
      .when([CreateActivityDefaultFormKeys.units], {
        is: (unitValue: string) => {
          return unitValue === UnitTypesKey.Minutes;
        },
        then: (schema) =>
          schema.required(commonValidationMessages.requiredValidation),
      })
      .default(""),
    [CreateActivityDefaultFormKeys.measurementOther]: yup
      .number()
      .nullable()
      .when([CreateActivityDefaultFormKeys.units], {
        is: (unitValue: string) => {
          return (
            unitValue === UnitTypesKey.Count || unitValue === UnitTypesKey.Days
          );
        },
        then: (schema) =>
          schema
            .required(commonValidationMessages.requiredValidation)
            .typeError(commonValidationMessages.requiredValidation)
            .min(1, commonValidationMessages.valueRangeFieldValidator1To99)
            .max(99, commonValidationMessages.valueRangeFieldValidator1To99),
      })
      .default(0),
  });
